import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Check, ShoppingBag } from 'lucide-react';
import type { Artwork } from '@/types';
import { useTranslation, useCurrency } from '@/hooks/useTranslation';
import { useCartStore, useAppStore } from '@/lib/store';
import { useUserStore } from '@/lib/user-store';
import ContactInquiryModal from '@/components/ContactInquiryModal';

const HIGH_PRICE_GEL_THRESHOLD = 3000;

interface ArtworkCardProps {
  artwork: Artwork;
  /** When true, hide the Add to Cart button (display-only mode for gated areas like the homepage preview). */
  displayOnly?: boolean;
}

const ArtworkCard: React.FC<ArtworkCardProps> = ({ artwork, displayOnly = false }) => {
  const navigate = useNavigate();
  const { t, localize } = useTranslation();
  const { formatPrice } = useCurrency();
  const addItem = useCartStore((s) => s.addItem);
  const cartQty = useCartStore((s) => s.items.filter((i) => i.artwork._id === artwork._id).reduce((sum, i) => sum + i.quantity, 0));
  const currentUser = useUserStore((s) => s.currentUser);
  const toggleWishlist = useUserStore((s) => s.toggleWishlist);
  const toggleGuestWishlist = useUserStore((s) => s.toggleGuestWishlist);
  const guestWishlist = useUserStore((s) => s.guestWishlist);
  const [justAdded, setJustAdded] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const maxStock = artwork.rarity === 'unique' ? 1 : artwork.edition ? Math.max(0, artwork.edition.total - artwork.edition.current + 1) : 99;
  const isOutOfStock = cartQty >= maxStock;

  // Convert price to GEL — if over threshold, show "Contact Us" instead of "Add to Cart"
  const exchangeRate = useAppStore((s) => s.exchangeRate);
  const usdAmount = artwork.price.currency === 'GEL' && exchangeRate > 0 ? artwork.price.amount / exchangeRate : artwork.price.amount;
  const gelPrice = (usdAmount / 100) * (exchangeRate || 2.7);
  const isHighPrice = gelPrice >= HIGH_PRICE_GEL_THRESHOLD;

  const wishlisted = currentUser
    ? currentUser.wishlist.includes(artwork._id)
    : guestWishlist.includes(artwork._id);

  const artistName =
    typeof artwork.artist === 'string'
      ? artwork.artist
      : localize(artwork.artist.name);

  return (
    <div
      className="group cursor-pointer"
      onClick={() => navigate(`/artwork/${artwork.slug}`)}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#f9f9f9] dark:bg-[#0a0a0a] mb-3 rounded-xl border border-black/5 dark:border-white/5 transition-all duration-500 group-hover:shadow-xl group-hover:shadow-black/5 dark:group-hover:shadow-white/5">
        {artwork.images?.[0] ? (
          <img
            src={artwork.images?.[0] || ''}
            className="w-full h-full object-cover grayscale-[0.2] transition-transform duration-700 group-hover:scale-110"
            alt={localize(artwork.title)}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-[#f0f0f0] dark:bg-[#111] flex items-center justify-center">
            <span className="text-[9px] font-bold uppercase tracking-widest text-black/10 dark:text-white/10">Loading</span>
          </div>
        )}
        <div className="absolute inset-0 bg-white/0 dark:bg-black/0 group-hover:bg-white/10 dark:group-hover:bg-black/10 transition-colors" />

        {!displayOnly && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (isOutOfStock) return;
              if (isHighPrice) {
                setContactOpen(true);
                return;
              }
              addItem(artwork, 'unframed');
              setJustAdded(true);
              setTimeout(() => setJustAdded(false), 1500);
            }}
            disabled={isOutOfStock}
            className={`absolute bottom-3 left-3 right-3 py-2 backdrop-blur-md rounded-lg opacity-100 translate-y-0 lg:opacity-0 lg:translate-y-2 lg:group-hover:opacity-100 lg:group-hover:translate-y-0 transition-all font-bold uppercase tracking-widest text-[8px] shadow-lg flex items-center justify-center gap-1.5 disabled:opacity-50 ${
              justAdded
                ? 'bg-black dark:bg-white text-white dark:text-black'
                : isOutOfStock
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                  : isHighPrice
                    ? 'bg-black dark:bg-white text-white dark:text-black'
                    : 'bg-white/90 dark:bg-black/90 text-black dark:text-white'
            }`}
          >
            <AnimatePresence mode="wait">
              {isOutOfStock ? (
                <motion.span key="oos" initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex items-center gap-1.5">
                  Out of Stock
                </motion.span>
              ) : isHighPrice ? (
                <motion.span key="contact" initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex items-center gap-1.5">
                  Contact Us
                </motion.span>
              ) : justAdded ? (
                <motion.span key="added" initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.5, opacity: 0 }} transition={{ duration: 0.2 }} className="flex items-center gap-1.5">
                  <Check size={12} strokeWidth={3} /> Added
                </motion.span>
              ) : (
                <motion.span key="add" initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.5, opacity: 0 }} transition={{ duration: 0.2 }} className="flex items-center gap-1.5">
                  <ShoppingBag size={10} /> Add to Cart
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        )}

        {/* Added to cart overlay flash */}
        <AnimatePresence>
          {justAdded && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 bg-black/40 dark:bg-white/20 rounded-xl flex items-center justify-center pointer-events-none"
            >
              <motion.div
                initial={{ scale: 0, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0 }}
                transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                className="w-14 h-14 rounded-full bg-white dark:bg-black flex items-center justify-center shadow-2xl"
              >
                <Check size={24} strokeWidth={3} className="text-black dark:text-white" />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Wishlist heart */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (currentUser) {
              toggleWishlist(artwork._id);
            } else {
              toggleGuestWishlist(artwork._id);
            }
          }}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full backdrop-blur-sm flex items-center justify-center transition-all z-10 ${
            wishlisted
              ? 'bg-red-500/90 text-white shadow-md'
              : 'bg-white/70 dark:bg-black/70 text-black/40 dark:text-white/40 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 hover:text-red-500'
          }`}
        >
          <Heart size={14} fill={wishlisted ? 'currentColor' : 'none'} />
        </button>

        {artwork.rarity === 'unique' && (
          <div className="absolute top-3 left-3 px-2 py-1 bg-black/80 dark:bg-white/80 text-white dark:text-black rounded-full text-[8px] font-bold uppercase tracking-wider">
            Unique
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1 px-1">
        <h3 className="text-[11px] font-bold uppercase tracking-tight text-black dark:text-white line-clamp-1 transition-colors duration-700">
          {localize(artwork.title)}
        </h3>
        {artwork.seriesName && (localize(artwork.seriesName)?.trim()) && (
          <p className="text-[8px] uppercase tracking-[0.2em] text-black/30 dark:text-white/30 truncate italic">
            {localize(artwork.seriesName)}
          </p>
        )}
        <p className="text-[9px] text-black/40 dark:text-white/40 tracking-wider truncate">
          {artistName}
        </p>
        <div className="flex justify-between items-center">
          <p className="text-[8px] font-bold text-black/30 dark:text-white/30 uppercase tracking-widest transition-colors duration-700">
            {artwork.category}
          </p>
          <span className="text-[10px] font-bold font-syncopate text-black dark:text-white transition-colors duration-700">
            {formatPrice(artwork.price.amount, artwork.price.currency)}
          </span>
        </div>
      </div>
      <ContactInquiryModal isOpen={contactOpen} onClose={() => setContactOpen(false)} artwork={artwork} />
    </div>
  );
};

export default ArtworkCard;
